import { To, useLocation, useSearchParams } from "react-router-dom";
import { useAppStore } from "store/store";
import usePaxSession from "../usePaxSession";
import Content, { ContentNS } from "content/content";
import { AllowedDocumentType } from "models/documents/allowed-document";
import { getRedirectURLForItemType } from "./useItemLevelRedirect";
import { ChecklistItemType } from "models/checklist/checklist-item/checklist-item-type";
import ChecklistResolutionStatus from "models/checklist/checklist-resolution-status";
import { getPaxSessionRelativeUrl } from "./useFlowRedirect";

export enum PageType {
  QuestionsAll = "QuestionsAll",
  QuestionsHaveDocument = "QuestionsHaveDocument",
  QuestionsDeclarations = "QuestionsDeclarations",
  TravelDocPage = "TravelDocPage",
  NonTravelDocPageWithinWizard = "NonTravelDocPageWithinWizard",
  NonTravelDocPageFromChecklist = "NonTravelDocPageFromChecklist",
  Checklist = "Checklist",
  Address = "Address",
}

export interface NavigateBackInfo {
  title: string;
  to: To;
  state?: any;
}

/*
Hook used to provide information on "Back to ..." link
for a specific page (pageType) the user in on, given the
context
 */
const useNavigateBackInfo = (
  pageType: PageType
): NavigateBackInfo | undefined => {
  const textUI = (s: string) => Content.shared.t(s, { ns: ContentNS.ui });

  const sessionId = useAppStore((state) => state.sessionId);

  const paxSession = usePaxSession()!;

  const [searchParams] = useSearchParams();

  // delete forceDocumentType from search params - doesn't change actual URL
  searchParams.delete("forceDocumentType");

  const paxSessionRelativeUrl = getPaxSessionRelativeUrl(
    sessionId,
    paxSession.paxSessionId
  );

  // don't show back button on scanner page - it has its own button,
  // as requires some additional camera handling
  const location = useLocation();
  const isScanning = location.pathname.includes("/capture");

  const travelDocIsPassport =
    paxSession.checklist.allowedTravelDocumentsSet.allowedDocuments.every(
      (doc) => doc.type === AllowedDocumentType.Passport
    );

  const haveDocumentQuestions = paxSession.checklist.questions.filter(
    (q) => q.type === "have-document-question"
  );

  // default redirect parameters for travel doc page
  const getTravelDocRedirect = () => ({
    title: travelDocIsPassport
      ? textUI("back-to.passport")
      : textUI("back-to.travel-document"),
    to: {
      pathname: getRedirectURLForItemType(
        sessionId,
        paxSession,
        ChecklistItemType.TravelDoc
      ),
      search: searchParams.toString(),
    },
    state: undefined,
  });

  switch (pageType) {
    case PageType.TravelDocPage:
      // travel doc page
      // no back link
      return undefined;

    case PageType.QuestionsHaveDocument:
      // questions page ("have document" only)
      // back is to travel document page
      return getTravelDocRedirect();

    case PageType.QuestionsDeclarations:
      // questions page ("declaration" - only remaining questions, in the form of declaration)
      if (haveDocumentQuestions.length > 0) {
        // there are "have document" questions
        return {
          title: textUI("back-to.questions-have-document"),
          to: {
            pathname: `${paxSessionRelativeUrl}/questions`,
            search: searchParams.toString(),
          },
          state: {
            showOnlyHaveDocumentQuestions: true,
            isEditing: true,
          },
        };
      }

      // no have document questions, send to passport/travel doc
      return getTravelDocRedirect();

    // non-travel doc page within wizard
    case PageType.NonTravelDocPageWithinWizard:
      if (
        paxSession.checklist.resolutionStatus ===
        ChecklistResolutionStatus.ReadyToFly
      ) {
        // if ready to fly - the only back is to checklist
        return {
          title: textUI("back-to.checklist"),
          to: {
            pathname: `${paxSessionRelativeUrl}/checklist`,
            search: searchParams.toString(),
          },
        };
      }

      if (paxSession.checklist.questions.length > 0) {
        // there are questions
        if (isScanning) {
          // don't show back link when scanning
          return undefined;
        }

        return {
          title: textUI("back-to.questions-have-document"),
          to: {
            pathname: `${paxSessionRelativeUrl}/questions`,
            search: searchParams.toString(),
          },
          state: {
            showOnlyHaveDocumentQuestions: true,
            isDeclaration: false,
            isEditing: true,
          },
        };
      }

      // no have document questions,
      // no other questions ->
      // send to passport/travel doc
      if (isScanning) {
        // don't show back link if scanning
        return undefined;
      }

      return getTravelDocRedirect();

    // non-travel doc page from checklist (or info)
    case PageType.NonTravelDocPageFromChecklist:
      if (isScanning) {
        // don't show back link if scanning
        return undefined;
      }

      return {
        title: textUI("back-to.checklist"),
        to: {
          pathname: `${paxSessionRelativeUrl}/checklist`,
          search: searchParams.toString(),
        },
      };

    case PageType.QuestionsAll:
      // back is to passport/travel doc
      return getTravelDocRedirect();

    case PageType.Address:
      // back is to questions page
      if (paxSession.checklist.questions.length > 0) {
        // there are questions
        if (isScanning) {
          // don't show back link if scanning
          return undefined;
        }

        return {
          title: textUI("back-to.questions-have-document"),
          to: {
            pathname: `${paxSessionRelativeUrl}/questions`,
            search: searchParams.toString(),
          },
          state: {
            showOnlyHaveDocumentQuestions:
              haveDocumentQuestions.length > 0 ? true : false,
            isDeclaration: haveDocumentQuestions.length > 0 ? false : true,
            isEditing: true,
          },
        };
      }

      // default is to passport/travel doc
      return getTravelDocRedirect();

    case PageType.Checklist:
      if (paxSession.checklist.questions.length > 0) {
        // there are questions
        if (isScanning) {
          // don't show back link if scanning
          return undefined;
        }

        return {
          title: textUI("back-to.edit"),
          to: {
            pathname: `${paxSessionRelativeUrl}/questions`,
            search: searchParams.toString(),
          },
          state: {
            showOnlyHaveDocumentQuestions: true,
            isDeclaration: false,
            isEditing: true,
          },
        };
      }

      // default is to passport/travel doc
      return getTravelDocRedirect();
  }
};

export default useNavigateBackInfo;
